/**
 * Agent-side WebSocket for Live Assist. Text frames carry UserToAgent JSON and,
 * with `decode=1`, canonical `{ type: "chunk", chunk }` messages for the live
 * Player. Raw JSON binary chunks remain supported by the test seam and local
 * transports. The agent sends AgentToUser JSON text frames. Reconnects with
 * exponential backoff.
 *
 * `LiveAgentSocket.WebSocketImpl` is injectable so tests can use a fake.
 */
import type { AgentToUser, Chunk, UserToAgent } from "@backstory/protocol";

export type SocketStatus = "connecting" | "open" | "closed" | "failed";

export interface LiveSocketEvents {
  message: UserToAgent;
  chunk: Chunk;
  status: SocketStatus;
  error: unknown;
}

type Listener<K extends keyof LiveSocketEvents> = (payload: LiveSocketEvents[K]) => void;

export interface WebSocketLike {
  readyState: number;
  binaryType: string;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export class LiveAgentSocket {
  /** Injectable for tests. */
  static WebSocketImpl: (new (url: string, protocols?: string[]) => WebSocketLike) | undefined =
    typeof WebSocket !== "undefined" ? (WebSocket as unknown as new (url: string, protocols?: string[]) => WebSocketLike) : undefined;

  /**
   * Keepalive cadence. The server closes an agent socket that stays silent for
   * 90 s (services/internal/live/sockets.go `socketIdle`), and a console that
   * is only watching sends nothing on its own. Without this ping every live
   * console dropped and reconnected every 90 s, losing the chunks published
   * in between and re-reading the session history on each reconnect.
   */
  static HEARTBEAT_MS = 30_000;

  private ws: WebSocketLike | null = null;
  private listeners = new Map<keyof LiveSocketEvents, Set<Listener<never>>>();
  private attempts = 0;
  private closedByUser = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private pending: string[] = [];
  private _status: SocketStatus = "closed";
  private decoder = typeof TextDecoder !== "undefined" ? new TextDecoder() : null;

  constructor(
    readonly url: string,
    private readonly maxAttempts = 6,
    /**
     * Subprotocols sent with the handshake. Token auth rides here because the
     * browser WebSocket API has no way to set an Authorization header.
     */
    private readonly protocols?: string[],
  ) {}

  get status(): SocketStatus {
    return this._status;
  }

  on<K extends keyof LiveSocketEvents>(type: K, fn: Listener<K>): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(fn as Listener<never>);
    return () => set!.delete(fn as Listener<never>);
  }

  private emit<K extends keyof LiveSocketEvents>(type: K, payload: LiveSocketEvents[K]): void {
    const set = this.listeners.get(type);
    if (!set) return;
    for (const fn of set) (fn as Listener<K>)(payload);
  }

  private setStatus(s: SocketStatus): void {
    if (this._status === s) return;
    this._status = s;
    this.emit("status", s);
  }

  connect(): void {
    const Impl = LiveAgentSocket.WebSocketImpl;
    if (!Impl) {
      this.setStatus("failed");
      this.emit("error", new Error("WebSocket is not available"));
      return;
    }
    this.closedByUser = false;
    this.setStatus("connecting");
    const ws = new Impl(this.url, this.protocols);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      this.setStatus("open");
      for (const m of this.pending.splice(0)) ws.send(m);
      this.startHeartbeat(ws);
    };
    ws.onmessage = (ev) => this.handle(ev.data);
    ws.onerror = (e) => this.emit("error", e);
    ws.onclose = () => {
      this.stopHeartbeat();
      this.ws = null;
      if (this.closedByUser) {
        this.setStatus("closed");
        return;
      }
      if (this.attempts >= this.maxAttempts) {
        this.setStatus("failed");
        return;
      }
      const delay = Math.min(30_000, 500 * 2 ** this.attempts++);
      this.setStatus("connecting");
      this.timer = setTimeout(() => this.connect(), delay);
    };
  }

  private handle(data: unknown): void {
    if (typeof data === "string") {
      let msg: UserToAgent | { type: string; chunk?: Chunk };
      try {
        msg = JSON.parse(data);
      } catch {
        return;
      }
      if (!msg || typeof msg !== "object" || !("type" in msg)) return;
      if (msg.type === "hello" || msg.type === "pong") return;
      if (msg.type === "chunk") {
        const chunk = msg.chunk;
        if (chunk && typeof chunk.seq === "number" && Array.isArray(chunk.events)) this.emit("chunk", chunk);
        return;
      }
      this.emit("message", msg as UserToAgent);
      return;
    }
    let text: string | null = null;
    if (data instanceof ArrayBuffer) text = this.decoder?.decode(new Uint8Array(data)) ?? null;
    else if (typeof Blob !== "undefined" && data instanceof Blob) {
      void data.text().then((t) => this.handleChunkText(t));
      return;
    } else if (data && typeof (data as ArrayBufferView).byteLength === "number") text = this.decoder?.decode(data as Uint8Array) ?? null;
    if (text != null) this.handleChunkText(text);
  }

  private handleChunkText(text: string): void {
    try {
      const chunk = JSON.parse(text) as Chunk;
      if (chunk && typeof chunk.seq === "number" && Array.isArray(chunk.events)) this.emit("chunk", chunk);
    } catch (e) {
      this.emit("error", e);
    }
  }

  private startHeartbeat(ws: WebSocketLike): void {
    this.stopHeartbeat();
    this.heartbeat = setInterval(() => {
      if (this.ws !== ws || ws.readyState !== 1) return;
      ws.send(JSON.stringify({ type: "ping" }));
    }, LiveAgentSocket.HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  /** Send a message to the end user's SDK (queued until the socket is open). */
  send(msg: AgentToUser): void {
    const text = JSON.stringify(msg);
    if (this.ws && this.ws.readyState === 1) this.ws.send(text);
    else this.pending.push(text);
  }

  close(): void {
    this.closedByUser = true;
    this.stopHeartbeat();
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.pending = [];
    this.ws?.close(1000, "agent left");
    this.ws = null;
    this.setStatus("closed");
  }
}
